// src/pages/PacientesPage.jsx
import { useState, useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../axiosInstance';
import PacienteList from '../components/PacienteList';
import AddPaciente from '../components/AddPaciente';
import { useClinic } from '../context/ClinicContext';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Link,
  CircularProgress,
  Paper,
  Fade,
  LinearProgress,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  Avatar,
  alpha,
  useMediaQuery,
  Menu,
  MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PeopleIcon from '@mui/icons-material/People';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { motion, AnimatePresence } from 'framer-motion';
const MotionPaper = motion(Paper);
const MotionBox = motion(Box);

const PacientesPage = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { authStatus, loading: authLoading, suscripcion } = useAuth();
  const { selectedClinicId } = useClinic();

  const [pacientes, setPacientes] = useState([]);
  const [loadingPacientes, setLoadingPacientes] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [fadeIn, setFadeIn] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Menú contextual móvil
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuPacienteId, setMenuPacienteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Control de peticiones en vuelo
  const fetchCtrlRef = useRef(null);

  useEffect(() => setFadeIn(true), []);

  const fetchPacientes = async (signal) => {
    setLoadingPacientes(true);
    try {
      const { data } = await axiosInstance.get('/pacientes/', {
        signal,
        meta: { skipClinicHeader: !selectedClinicId }
      });
      const items = Array.isArray(data) ? data : (data?.results ?? []);
      setPacientes(items);
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.name === 'AbortError' || e?.code === 'ERR_CANCELED') return;
      console.error('Error al obtener pacientes:', e);
      if (e?.response?.status === 429) {
        toast.warn('Demasiadas solicitudes. Espera unos segundos…');
      } else if (e?.response?.status === 401) {
        toast.error('Sesión expirada. Inicia sesión nuevamente.');
      } else {
        toast.error('No se pudieron cargar los pacientes');
      }
      setPacientes([]);
    } finally {
      setLoadingPacientes(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (authStatus === 'unauthorized') return;

    if (fetchCtrlRef.current) {
      fetchCtrlRef.current.abort();
      fetchCtrlRef.current = null;
    }

    setPacientes([]);

    const ctrl = new AbortController();
    fetchCtrlRef.current = ctrl;
    fetchPacientes(ctrl.signal);

    return () => {
      ctrl.abort();
      if (fetchCtrlRef.current === ctrl) fetchCtrlRef.current = null;
    };
  }, [authLoading, authStatus, selectedClinicId]);

  useEffect(() => {
    if (location.state?.fromAddCita) {
      setOpenAddModal(true);
      navigate(location.pathname, { replace: true, state: { returnToCitas: location.state.returnToCitas || false } });
    }
  }, [location.state, navigate]);

  const filteredPacientes = useMemo(() => {
    const s = (searchTerm || '').toLowerCase();
    return pacientes.filter((paciente) => {
      const nombre = `${paciente.nombres || ''} ${paciente.apellidos || ''}`.trim().toLowerCase();
      const dni = (paciente.dni || '').toLowerCase();
      return nombre.includes(s) || dni.includes(s);
    });
  }, [pacientes, searchTerm]);

  const handleOpen = () => setOpenAddModal(true);
  const handleClose = () => setOpenAddModal(false);

  const handlePacienteAdded = async (newPacienteId) => {
    if (fetchCtrlRef.current) {
      fetchCtrlRef.current.abort();
      fetchCtrlRef.current = null;
    }
    const ctrl = new AbortController();
    fetchCtrlRef.current = ctrl;
    await fetchPacientes(ctrl.signal);
    handleClose();
    if (location.state?.returnToCitas) {
      navigate('/doctor/citas', {
        state: {
          fromAddPaciente: true,
          pacienteCreadoId: newPacienteId,
          openAddCitaModal: true,
        },
      });
    }
  };

  const openMenu = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuPacienteId(id);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuPacienteId(null);
  };

  const handleDeletePaciente = async (id) => {
    if (!id) return;
    if (!window.confirm('¿Deseas eliminar este paciente? Esta acción no se puede deshacer.')) return;
    setDeleting(true);
    try {
      await axiosInstance.delete(`/pacientes/${id}/`, {
        meta: { skipClinicHeader: !selectedClinicId }
      });
      toast.success('Paciente eliminado.');
      if (fetchCtrlRef.current) {
        fetchCtrlRef.current.abort();
        fetchCtrlRef.current = null;
      }
      const ctrl = new AbortController();
      fetchCtrlRef.current = ctrl;
      await fetchPacientes(ctrl.signal);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.error || 'No se pudo eliminar el paciente.';
      toast.error(msg);
    } finally {
      setDeleting(false);
      closeMenu();
    }
  };

  if (authLoading || loadingPacientes) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
            theme.palette.secondary.main,
            0.05
          )} 100%)`,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            Cargando pacientes...
          </Typography>
        </Stack>
      </Box>
    );
  }

  const limiteTotal = suscripcion?.plan ? suscripcion.plan.limite_pacientes : 5;
  const limiteAlcanzado = pacientes.length >= limiteTotal;
  const porcentajeUso = Math.min(100, (pacientes.length / Math.max(limiteTotal, 1)) * 100);

  const getInitials = (nombres = '', apellidos = '') => {
    const n = (nombres || '').trim().split(' ')[0] || '';
    const a = (apellidos || '').trim().split(' ')[0] || '';
    return (n[0] || '').toUpperCase() + (a[0] || '').toUpperCase();
  };
  const calcEdad = (fecha) => {
    if (!fecha) return null;
    try {
      const f = new Date(fecha);
      const diff = Date.now() - f.getTime();
      const ageDt = new Date(diff);
      return Math.abs(ageDt.getUTCFullYear() - 1970);
    } catch {
      return null;
    }
  };

  // Función segura para ID/Key
  const getSafeId = (p, idx) => {
    const raw = p?.id_paciente ?? p?.id ?? p?.uuid ?? p?.pk ?? '';
    const str = typeof raw === 'number' ? String(raw) : (raw || '').toString();
    const trimmed = (str || '').trim();
    if (trimmed) return trimmed;
    if (import.meta?.env?.MODE !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[PacientesPage] Paciente sin ID. Usando idx como fallback:', idx, p);
    }
    return `temp-${idx}`;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(
          theme.palette.secondary.main,
          0.05
        )} 100%)`,
        py: { xs: 3, md: 6 },
      }}
    >
      {/* Encabezado */}
      <MotionPaper
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          mb: 3,
          mx: { xs: 2, md: 3 },
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: alpha('#fff', 0.1),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: alpha('#fff', 0.08),
          }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              bgcolor: alpha('#fff', 0.2),
              color: 'white',
              borderRadius: '50%',
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 8px 32px ${alpha('#000', 0.2)}`,
            }}
          >
            <PeopleIcon sx={{ fontSize: 34 }} />
          </Box>
          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant={isSmDown ? 'h5' : 'h4'} sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}>
              Gestión de Pacientes
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Administra la información de tus pacientes
            </Typography>
          </Box>
          <Tooltip title={limiteAlcanzado ? 'Has alcanzado el límite de tu plan' : 'Añadir nuevo paciente'}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleOpen}
                disabled={limiteAlcanzado}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  '&:hover': { bgcolor: 'grey.100', transform: 'translateY(-2px)', boxShadow: '0 6px 25px rgba(0,0,0,0.2)' },
                  transition: 'all 0.25s ease',
                  '&.Mui-disabled': {
                    bgcolor: alpha('#fff', 0.5),
                    color: alpha('#000', 0.4),
                  },
                }}
              >
                Añadir Paciente
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </MotionPaper>

      {/* Panel de estado y límite */}
      <MotionPaper
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.05 }}
        elevation={0}
        sx={{
          mx: { xs: 2, md: 3 },
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          background: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" mb={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Pacientes Registrados
          </Typography>
          <Chip
            label={`${pacientes.length} / ${limiteTotal}`}
            color={limiteAlcanzado ? 'warning' : 'primary'}
            sx={{
              fontWeight: 700,
              bgcolor: limiteAlcanzado
                ? alpha(theme.palette.warning.main, 0.12)
                : alpha(theme.palette.primary.main, 0.12),
            }}
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          value={porcentajeUso}
          color={porcentajeUso > 80 ? 'warning' : 'primary'}
          sx={{
            height: 10,
            borderRadius: 5,
            backgroundColor: alpha(theme.palette.text.primary, 0.08),
            '& .MuiLinearProgress-bar': { borderRadius: 5 },
          }}
        />
        {limiteAlcanzado && (
          <Alert
            severity="warning"
            variant="outlined"
            sx={{
              mt: 2,
              borderRadius: 3,
              '& .MuiAlert-icon': { color: 'warning.dark' },
            }}
          >
            Has llegado al límite de pacientes de tu plan.
            <Link
              component={RouterLink}
              to="/doctor/planes"
              sx={{
                fontWeight: 'bold',
                ml: 0.5,
                '&:hover': { color: 'primary.dark', textDecoration: 'none' },
              }}
            >
              ¡Actualiza tu plan para añadir más!
            </Link>
          </Alert>
        )}
      </MotionPaper>

      {/* Buscador */}
      <MotionPaper
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.08 }}
        elevation={0}
        sx={{
          mx: { xs: 2, md: 3 },
          mb: 3,
          p: { xs: 1.5, md: 2 },
          borderRadius: 4,
          background: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
        }}
      >
        <TextField
          fullWidth
          placeholder="Buscar por nombre o DNI…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size={isSmDown ? 'small' : 'medium'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton aria-label="limpiar" onClick={() => setSearchTerm('')} edge="end">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      </MotionPaper>

      {/* Barra móvil de estado + acciones rápidas */}
      {isMdDown && (
        <MotionBox
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
          sx={{ mx: { xs: 2, md: 3 }, mb: 2 }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Mostrando {filteredPacientes.length} de {pacientes.length} pacientes
            </Typography>
            <Box display="flex" gap={1}>
              <Button size="small" variant="outlined" startIcon={<FilterListIcon />} onClick={() => toast.info('Filtros próximamente')} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Filtrar
              </Button>
              <Button size="small" variant="outlined" startIcon={<SortIcon />} onClick={() => toast.info('Ordenar próximamente')} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Ordenar
              </Button>
            </Box>
          </Box>
        </MotionBox>
      )}

      {/* Lista de pacientes */}
      <Fade in={fadeIn} timeout={500}>
        <Box sx={{ mx: { xs: 2, md: 3 }, minWidth: 0 }}>
          {!isMdDown ? (
            <PacienteList
              pacientes={filteredPacientes}
              onListChange={() => {
                if (fetchCtrlRef.current) {
                  fetchCtrlRef.current.abort();
                  fetchCtrlRef.current = null;
                }
                const ctrl = new AbortController();
                fetchCtrlRef.current = ctrl;
                fetchPacientes(ctrl.signal);
              }}
            />
          ) : (
            <AnimatePresence mode="wait">
              <Stack spacing={1.5}>
                {filteredPacientes.length === 0 ? (
                  <MotionPaper
                    key="empty-state"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35 }}
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      borderRadius: 4,
                      border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
                      background: theme.palette.background.paper,
                    }}
                  >
                    <Typography color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                      No hay pacientes que coincidan con tu búsqueda.
                    </Typography>
                  </MotionPaper>
                ) : (
                  filteredPacientes.map((p, idx) => {
                    // ID seguro
                    const safeId = getSafeId(p, idx);
                    const id = safeId.startsWith('temp-') ? null : safeId;

                    // Campos para key
                    const dni = p.dni || p.identidad || p.numero_identidad || '';
                    const dniKeyPart = (dni || 'nodni').toString().trim() || 'nodni';

                    // Key robusta con fallback
                    let uniqueKey = `pac-${safeId}-${dniKeyPart}-${idx}`;
                    if (!uniqueKey || uniqueKey.trim() === '') {
                      uniqueKey = `pac-fallback-${idx}`;
                    }

                    const nombreCompleto = `${p.nombres || ''} ${p.apellidos || ''}`.trim();
                    const edad = calcEdad(p.fecha_nacimiento);
                    const telefono = p.telefono || p.celular || p.telefono_movil || p.telefono_principal || '';
                    const email = p.email || p.correo || '';

                    return (
                      <MotionPaper
                        key={uniqueKey}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.35, delay: Math.min(idx * 0.02, 0.2) }}
                        whileHover={{ y: -2 }}
                        sx={{
                          p: 1.75,
                          borderRadius: 4,
                          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                          background: theme.palette.background.paper,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 2, width: '100%' }}>
                          <Avatar
                            src={p.foto_perfil || p.foto_url || p.avatar_url || ''}
                            alt={nombreCompleto}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              flexShrink: 0,
                            }}
                            imgProps={{ onError: (e) => { e.currentTarget.src = ''; } }}
                          >
                            {getInitials(p.nombres, p.apellidos)}
                          </Avatar>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                lineHeight: 1.25,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                hyphens: 'auto',
                                pr: 1
                              }}
                              title={nombreCompleto}
                            >
                              {nombreCompleto || 'Paciente sin nombre'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {edad != null ? `${edad} años` : null}
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={(e) => openMenu(e, id)}
                            sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: -0.5 }}
                            aria-label="Opciones de paciente"
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                          {email ? (
                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                              <EmailOutlinedIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary" noWrap title={email} sx={{ flex: 1, minWidth: 0 }}>
                                {email}
                              </Typography>
                            </Box>
                          ) : null}

                          {telefono ? (
                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                              <PhoneOutlinedIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {telefono}
                              </Typography>
                            </Box>
                          ) : null}

                          {dni ? (
                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                              <BadgeOutlinedIcon fontSize="small" color="action" />
                              <Typography variant="body2" color="text.secondary">
                                {dni}
                              </Typography>
                            </Box>
                          ) : null}
                        </Stack>

                        <Divider sx={{ my: 1.25 }} />

                        <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => (id ? navigate(`/doctor/pacientes/${id}`) : toast.info('Paciente sin ID'))}
                            sx={{
                              borderRadius: '18px',
                              textTransform: 'none',
                              minWidth: 110,
                              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) }
                            }}
                          >
                            Ver
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              if (!id) return toast.info('Paciente sin ID');
                              navigate('/doctor/citas', { state: { pacientePreseleccionadoId: id, openAddCitaModal: true } });
                            }}
                            sx={{
                              borderRadius: '18px',
                              boxShadow: 'none',
                              textTransform: 'none',
                              minWidth: 120,
                              '&:hover': { boxShadow: theme.shadows[2] }
                            }}
                          >
                            Consulta
                          </Button>
                        </Box>
                      </MotionPaper>
                    );
                  })
                )}
              </Stack>
            </AnimatePresence>
          )}
        </Box>
      </Fade>

      {/* Menú contextual móvil */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 3,
            minWidth: 220,
            overflow: 'visible',
            mt: 1,
            boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {[
          <MenuItem
            key="menu-ver-detalles"
            onClick={() => {
              if (menuPacienteId) navigate(`/doctor/pacientes/${menuPacienteId}`);
              closeMenu();
            }}
            sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
          >
            Ver detalles
          </MenuItem>,

          <MenuItem
            key="menu-nueva-consulta"
            onClick={() => {
              if (!menuPacienteId) return;
              navigate('/doctor/citas', { state: { pacientePreseleccionadoId: menuPacienteId, openAddCitaModal: true } });
              closeMenu();
            }}
            sx={{ borderRadius: 2, mx: 1, my: 0.5 }}
          >
            Nueva consulta
          </MenuItem>,

          <Divider key="menu-divider" sx={{ my: 0.5 }} />,

          <MenuItem
            key="menu-eliminar"
            disabled={deleting}
            onClick={() => handleDeletePaciente(menuPacienteId)}
            sx={{
              color: 'warning.main',
              borderRadius: 2,
              mx: 1,
              my: 0.5,
              '&:hover': { backgroundColor: alpha(theme.palette.warning.main, 0.08) }
            }}
          >
            {deleting ? 'Eliminando…' : 'Eliminar'}
          </MenuItem>
        ]}
      </Menu>

      {/* Diálogo para añadir paciente */}
      <Dialog
        open={openAddModal}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
                fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : 4,
            boxShadow: theme.shadows[6],
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            color: theme.palette.text.primary,
            py: { xs: 1.5, md: 2 },
            px: { xs: 2, md: 3 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backdropFilter: 'saturate(1.2) blur(2px)'
          }}
        >
          Añadir Nuevo Paciente
          <IconButton edge="end" onClick={handleClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, md: 3 }, mt: 1 }}>
          <AddPaciente onListChange={handlePacienteAdded} onClose={handleClose} />
        </DialogContent>

        <DialogActions
          sx={{
            p: { xs: 2, md: 2 },
            position: isSmDown ? 'sticky' : 'static',
            bottom: 0,
            backgroundColor: theme.palette.background.paper,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`
          }}
        >
          <Button onClick={handleClose} startIcon={<CloseIcon />} sx={{ borderRadius: '20px' }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PacientesPage;

